import { t } from "@lingui/macro";
import { Brain, Sparkle } from "@phosphor-icons/react";
import { Badge, Button } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { motion } from "framer-motion";

import { useTemplateRecommendations } from "../../hooks/smart-ai/use-template-recommendations";

/* eslint-disable lingui/no-unlocalized-strings */

type AiTemplateBannerProps = {
  jobRole?: string;
  yearsOfExperience?: number;
  currentTemplate?: string;
  className?: string;
  onTemplateSelect: (templateName: string) => void;
  onColorSelect: (color: string) => void;
};

export const AiTemplateBanner = ({
  jobRole,
  yearsOfExperience,
  currentTemplate,
  className,
  onTemplateSelect,
  onColorSelect,
}: AiTemplateBannerProps) => {
  const { recommendations, colorRecommendations, hasRecommendations } = useTemplateRecommendations({
    jobRole,
    yearsOfExperience,
  });

  if (!hasRecommendations || !jobRole) {
    return null;
  }

  const topRecommendation = recommendations[0];
  const isRecommendedSelected =
    topRecommendation && currentTemplate === topRecommendation.templateName;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "mb-4 rounded-lg border-2 border-primary/20 bg-primary/5 p-4 dark:bg-primary/10",
        className,
      )}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2 sm:mb-3 sm:gap-3">
        <div className="flex items-center gap-1.5 rtl:flex-row-reverse sm:gap-2">
          <Brain size={16} className="text-primary sm:size-[18px]" />
          <span className="text-xs font-medium sm:text-sm">{t`AI Recommendation`}</span>
          <Badge variant="secondary" className="px-1.5 text-[10px] sm:px-2 sm:text-xs">
            {t`Beta`}
          </Badge>
        </div>
      </div>

      {topRecommendation && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-1.5 rtl:flex-row-reverse sm:gap-2">
              <Sparkle size={12} className="text-primary sm:size-[14px]" />
              <span className="text-xs font-medium capitalize sm:text-sm">
                {topRecommendation.templateName}
              </span>
              <Badge className="bg-green-100 px-1.5 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200 sm:text-xs">
                {topRecommendation.score}% Match
              </Badge>
            </div>
            <p className="line-clamp-2 text-[10px] text-muted-foreground sm:text-xs">{topRecommendation.reasons[0]}</p>
          </div>

          <div className="flex items-center gap-1.5 rtl:flex-row-reverse sm:ml-4 sm:rtl:mr-4 sm:rtl:ml-0 sm:gap-2">
            {!isRecommendedSelected && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[10px] sm:h-8 sm:px-3 sm:text-xs"
                onClick={() => {
                  onTemplateSelect(topRecommendation.templateName);
                }}
              >
                {t`Try This`}
              </Button>
            )}

            {colorRecommendations.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-1.5 text-[10px] rtl:flex-row-reverse sm:h-8 sm:px-2 sm:text-xs"
                onClick={() => {
                  onColorSelect(colorRecommendations[0].primary);
                }}
              >
                <div
                  className="mr-1 size-3 rounded-full border sm:size-4 rtl:ml-1 rtl:mr-0"
                  style={{ backgroundColor: colorRecommendations[0].primary }}
                />
                {t`AI Color`}
              </Button>
            )}
          </div>
        </div>
      )}

      {isRecommendedSelected && (
        <div className="mt-2 rounded border border-green-200 bg-green-50 p-2 dark:border-green-800 dark:bg-green-900/20">
          <div className="flex items-center gap-2">
            <Sparkle size={14} className="text-green-600" />
            <span className="text-xs font-medium text-green-800 dark:text-green-200">
              {t`Perfect! You're using our AI-recommended template.`}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};
